'use client';

import { useState, useRef } from 'react';
import type { IComment } from '@/lib/types/comment';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FiEdit2, FiTrash2, FiMessageSquare } from 'react-icons/fi';
import { format } from 'date-fns';
import { cn, formatRelativeTime } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';

interface CommentItemProps {
  comment: IComment;
  onReply: (parentId: number, content: string) => void;
  onUpdate: (id: number, content: string) => void;
  onDelete: (id: number) => void;
  currentUserId: number;
}

export const CommentItem = ({
  comment,
  onReply,
  onUpdate,
  onDelete,
  currentUserId,
}: CommentItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [replyContent, setReplyContent] = useState('');
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const handleUpdate = () => {
    onUpdate(comment.id, editContent);
    setIsEditing(false);
  };

  const handleReply = () => {
    onReply(comment.id, replyContent);
    setIsReplying(false);
    setReplyContent('');
  };

  const handleReplyClick = () => {
    setIsReplying(!isReplying);
    setTimeout(() => {
      if (!isReplying) {
        replyTextareaRef.current?.focus();
      }
    }, 0);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    action: 'reply' | 'update'
  ) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (action === 'reply' && replyContent.trim()) {
        handleReply();
      } else if (action === 'update' && editContent.trim()) {
        handleUpdate();
      }
    }
  };

  const isOwner = currentUserId === comment.user_id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-4"
    >
      <Card className="p-4 transition-shadow duration-200 hover:shadow-md">
        <div className="flex flex-row gap-4 sm:items-start">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={comment.user.avatar} alt={comment.user.name} />
            <AvatarFallback>{comment.user.name.charAt(0)}</AvatarFallback>
          </Avatar>
          
          <div className="w-full flex-1 space-y-3">
            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
              <div>
                <p className="font-semibold text-sm sm:text-base">{comment.user.name}</p>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {formatRelativeTime(comment.created_at)}
                </p>
              </div>
              
              {isOwner && (
                <div className="flex gap-1 self-end sm:self-start">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                    className="h-8 w-8 p-0"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(comment.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <AnimatePresence mode="wait">
              {isEditing ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-2"
                >
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'update')}
                    className="min-h-[100px] resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleUpdate}>
                      Update
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm sm:text-base"
                >
                  {comment.content}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-primary"
                onClick={handleReplyClick}
              >
                <FiMessageSquare className="mr-2 h-4 w-4" />
                Trả lời
              </Button>
            </div>

            <AnimatePresence>
              {isReplying && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2"
                >
                  <Textarea
                    ref={replyTextareaRef}
                    placeholder="Viết bình luận..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'reply')}
                    className="min-h-[100px] resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsReplying(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleReply}
                      disabled={!replyContent.trim()}
                    >
                      Reply
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Card>

      {comment.replies && comment.replies.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="ml-4 space-y-4 border-muted border-l-2 pl-4 sm:ml-2"
        >
          {comment.replies.filter(reply => reply?.id).map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onUpdate={onUpdate}
              onDelete={onDelete}
              currentUserId={currentUserId}
            />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}; 